import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	EventEmitter,
	Input,
	Output,
	Signal,
	computed,
	effect,
	inject,
	signal,
	untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LucideAngularModule } from 'lucide-angular';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
@Component({
	selector: 'app-user-settings-contacts',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCardModule,
		MatSelectModule,
		MatTableModule,
		LucideAngularModule,
		MatButtonModule,
		MatTooltipModule
	],
	templateUrl: './user-settings_contacts.html',
	styleUrl: './user-settings_contacts.scss'
})
export class UserSettingsContactsComponent {
	@Input({ required: true }) requestForm!: Signal<boolean>;
	@Input({ required: true }) contactsData!: Signal<any | null>;
	@Input() modoEdicion = false;
	@Input() ocultarEmail = false;
	@Input() soloLectura = true;

	mostrarFormAgregar = signal(false);

	@Output() contactsChange = new EventEmitter<any>();

	private fb = inject(FormBuilder);
	private destroyRef = inject(DestroyRef);

	contactForm: FormGroup;

	contacts = signal<any[]>([]);
	originalContacts = signal<any[]>([]);

	dataSource = new MatTableDataSource<any>();
	displayedColumns = ['tipoContacto', 'valor', 'accion'];

	modoEdicionContacto = signal(false);
	contactoEditIndex = signal<number | null>(null);

	showHasChanges = signal(false);

	tiposContacto = [
		{ id: 'correoElectronico', description: 'Correo electrónico' },
		{ id: 'celular', description: 'Celular' },
		{ id: 'telefono', description: 'Teléfono' },
		{ id: 'telefonoLaboral', description: 'Teléfono laboral' }
	];

	tiposContactoFiltered = computed(() => {
		const usados = this.contacts().map((c) => c.tipoContacto);
		const edit = this.contactoEditIndex();

		return this.tiposContacto.filter(
			(t) =>
				(!usados.includes(t.id) || usados.indexOf(t.id) === edit) &&
				!(this.ocultarEmail && t.id === 'correoElectronico')
		);
	});

	labelValorContacto = signal('Correo electrónico');
	placeholderValorContacto = signal('email@example.com');

	constructor() {
		this.contactForm = this.createForm();

		/** Cargar data inicial */
		effect(() => {
			const data = this.contactsData();
			if (!data) return;

			this.parseContactsFromData(data);
			untracked(() => {
				this.originalContacts.set([...this.contacts()]);
				this.dataSource.data = [...this.contacts()];
				this.showHasChanges.set(false);
			});
		});

		/** Enviar al submit del padre */
		effect(() => {
			if (this.requestForm()) {
				this.sendFormData();
			}
		});

		this.contactForm.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.checkForChanges());

		this.contactForm
			.get('tipoContacto')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((tipo) => this.updateContactField(tipo));
	}

	private createForm(): FormGroup {
		return this.fb.group({
			tipoContacto: ['', Validators.required],
			valorContacto: ['', Validators.required],
			emailValidado: ['0']
		});
	}

	resetForm(): void {
		// intentionally empty
	}

	private parseContactsFromData(data: any): void {
		const parsed: any[] = [];

		if (data.correoElectronico) {
			parsed.push({
				tipoContacto: 'correoElectronico',
				valor: data.correoElectronico,
				emailValidado: data.emailValidado ?? '0'
			});
		}
		if (data.celular) parsed.push({ tipoContacto: 'celular', valor: data.celular });
		if (data.telefono) parsed.push({ tipoContacto: 'telefono', valor: data.telefono });
		if (data.telefonoLaboral)
			parsed.push({ tipoContacto: 'telefonoLaboral', valor: data.telefonoLaboral });

		this.contacts.set(parsed);
	}

	updateContactField(tipo: string): void {
		const control = this.contactForm.get('valorContacto');
		control?.clearValidators();

		switch (tipo) {
			case 'correoElectronico':
				this.labelValorContacto.set('Correo electrónico');
				this.placeholderValorContacto.set('email@example.com');
				control?.setValidators([Validators.required, Validators.email]);
				break;

			case 'celular':
				this.labelValorContacto.set('Celular');
				this.placeholderValorContacto.set('+5491169682324');
				control?.setValidators([Validators.required, Validators.pattern(/^\+?\d+$/)]);
				break;

			default:
				this.labelValorContacto.set('Teléfono');
				this.placeholderValorContacto.set('+541112345678');
				control?.setValidators([Validators.required, Validators.pattern(/^\+?\d+$/)]);
		}

		control?.updateValueAndValidity();
	}

	agregarNuevoContacto(): void {
		if (this.contactForm.invalid) {
			this.contactForm.markAllAsTouched();
			return;
		}

		const v = this.contactForm.value;

		const nuevo = {
			tipoContacto: v.tipoContacto,
			valor: v.valorContacto,
			emailValidado: v.emailValidado ?? '0'
		};

		const updated = [...this.contacts(), nuevo];
		this.contacts.set(updated);
		this.dataSource.data = updated;

		this.contactForm.reset();
		this.mostrarFormAgregar.set(false);
		this.showHasChanges.set(true);
		this.emitContactsChange();
	}

	editarContacto(contacto: any, index: number): void {
		this.modoEdicionContacto.set(true);
		this.contactoEditIndex.set(index);
		this.contactForm.patchValue({
			tipoContacto: contacto.tipoContacto,
			valorContacto: contacto.valor,
			emailValidado: contacto.emailValidado ?? '0'
		});
	}

	guardarEdicionContacto(): void {
		if (this.contactForm.invalid || this.contactoEditIndex() === null) return;

		const index = this.contactoEditIndex()!;
		const v = this.contactForm.value;

		const updated = [...this.contacts()];
		updated[index] = {
			tipoContacto: v.tipoContacto,
			valor: v.valorContacto,
			emailValidado: v.emailValidado ?? '0'
		};

		this.contacts.set(updated);
		this.dataSource.data = updated;

		this.cancelarEdicion();
		this.showHasChanges.set(true);
		this.emitContactsChange();
	}

	eliminarContacto(index: number): void {
		const updated = this.contacts().filter((_, i) => i !== index);
		this.contacts.set(updated);
		this.dataSource.data = updated;

		this.showHasChanges.set(true);
		this.emitContactsChange();
	}

	cancelarEdicion(): void {
		this.contactForm.reset();
		this.modoEdicionContacto.set(false);
		this.mostrarFormAgregar.set(false);
		this.contactoEditIndex.set(null);
	}

	private checkForChanges(): void {
		this.showHasChanges.set(
			JSON.stringify(this.contacts()) !== JSON.stringify(this.originalContacts())
		);
	}

	private sendFormData(): void {
		if (this.contacts().length === 0) return;
		this.emitContactsChange();
	}

	private emitContactsChange(): void {
		const payload: any = {};
		this.contacts().forEach((c) => {
			payload[c.tipoContacto] = c.valor;
			if (c.tipoContacto === 'correoElectronico') {
				payload.emailValidado = c.emailValidado;
			}
		});

		this.contactsChange.emit(payload);
	}

	getTipoContactoDescription(tipo: string): string {
		return this.tiposContacto.find((t) => t.id === tipo)?.description ?? '—';
	}

	getInputType(tipo: string | null): string {
		switch (tipo) {
			case 'correoElectronico':
				return 'email';

			case 'telefonoMovil':
			case 'telefonoFijo':
				return 'tel';

			default:
				return 'text';
		}
	}
}
